import { useCallback, useEffect, useState } from 'react';

import { WizardProposal, WizardTaskBuildProgress } from '../../entities/wizard-proposal';
import { getWizardProposal, retryWizardProposal } from '../api/wizard';
import { auth } from '../firebase/firebase';
import useSocket from './socket-hook';

// `enabled` gates the REST snapshot fetch — only worth doing when a wizard session is actually
// being viewed, not on every SessionChatView mount regardless of session type.
//
// `hasHistory` distinguishes "reopening a session that already has messages" from "a session that
// was just created". The backend keeps a single proposal per user (full-replace, no history — see
// wizard-contract.md §5), not one per session, so the REST snapshot on mount may belong to a
// different, older wizard attempt than the one being viewed. That's harmless to restore live when
// resuming unfinished work (draft/confirmed/building/failed) or reopening a session that already
// has this build's own messages — but for a brand-new, empty session, restoring a stale `built`
// snapshot would silently block starting a new spec (the panel would show old task/platform info
// with no draft UI). In that one case, expose it as `priorBuilt` instead so callers can surface it
// passively rather than feeding it into the live proposal panel.
export function useWizardProposal(enabled = true, hasHistory = true) {
  const userId = auth.currentUser?.uid ?? 'anonymous';
  const { on, off } = useSocket(userId);

  const [proposal, setProposal] = useState<WizardProposal | null>(null);
  const [priorBuilt, setPriorBuilt] = useState<WizardProposal | null>(null);
  const [taskProgress, setTaskProgress] = useState<Record<string, WizardTaskBuildProgress>>({});

  // Restore-on-reload: the WS event only fires on new mutations, so a fresh page load needs the
  // one-off snapshot read. If a live `wizardProposalUpdated` already arrived first, keep it.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    getWizardProposal()
      .then((p) => {
        if (cancelled || !p) return;
        if (p.status === 'built' && !hasHistory) {
          setPriorBuilt((prev) => prev ?? p);
        } else {
          setProposal((prev) => prev ?? p);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled, hasHistory]);

  useEffect(() => {
    const onProposalUpdated = (data: { userId: string; proposal: WizardProposal }) => {
      setProposal(data.proposal);
      // Live activity in this session supersedes the passive "previously built" summary.
      setPriorBuilt(null);
    };

    const onTaskProgress = (data: WizardTaskBuildProgress & { userId: string }) => {
      setTaskProgress((prev) => ({ ...prev, [data.taskId]: data }));
    };

    on('wizardProposalUpdated', onProposalUpdated);
    on('wizardTaskBuildProgress', onTaskProgress);

    return () => {
      off('wizardProposalUpdated', onProposalUpdated as (...args: unknown[]) => void);
      off('wizardTaskBuildProgress', onTaskProgress as (...args: unknown[]) => void);
    };
  }, [userId, on, off]);

  const clear = useCallback(() => {
    setProposal(null);
    setPriorBuilt(null);
    setTaskProgress({});
  }, []);

  const dismissPriorBuilt = useCallback(() => setPriorBuilt(null), []);

  const retry = useCallback(async () => {
    const p = await retryWizardProposal();
    setProposal(p);
    setPriorBuilt(null);
    return p;
  }, []);

  return { proposal, priorBuilt, dismissPriorBuilt, taskProgress, clear, retry };
}
